import React, {
    useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    FOCUS_AREAS, GOAL_CHOICES 
} from '../../config/types';
import dataDogLogger from '../../../../lib/datadog';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';
import {
    useCookies 
} from 'react-cookie';

type Props = {
    position: number
};

const stepTitles = ['Your Goals', 'Your Focus Areas', 'Your Starting Point', 'Where You\'re Headed'];

const useStyles = makeStyles((theme) => ({
    scrollableContainer: {
        overflow: 'scroll',
        scrollbarWidth: 'none',
        minHeight: '100%',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    textSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '24px'
    },
    progress: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        columnGap: '8px',
        margin: '32px 0'
    },
    circle: {
        width: '8px',
        height: '8px',
        borderRadius: '50%'
    },
    stepTitle: {
        fontWeight: TypographyWeights.BOLD,
        textAlign: 'center'
    },
    stepBody: {
        fontFamily: 'Poppins',
        fontSize: '14px',
        lineHeight: '20px',
        textAlign: 'center',
        color: theme.palette.neutral.main,
        margin: 0,
        marginTop: '6px'
    },
    goalContainer: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '24px',
        alignItems: 'center',
        justifyContent: 'center',
    },
    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '58px',
        height: '58px',
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        marginBottom: '8px'
    },
    topGoalIcon: {
        fill: theme.palette.white,
        width: '25px',
        height: '25px'
    },
    topGoalTitle: {
        textAlign: 'center'
    },
    divider: {
        width: '20%',
        height: '2px',
        backgroundColor: theme.palette.primary['100']
    },
    topGoalSubtitle: {
        textAlign: 'center'
    },
    otherGoals: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridGap: '8px',
        width: '100%'
    },
    otherGoal: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '12px',
        alignItems: 'center',
        padding: '12px 0px',
        backgroundColor: theme.palette.primary.light,
        borderRadius: '8px',
        width: '100%'
    },
    otherGoalIcon: {
        fill: theme.palette.primary.dark,
        width: '16px',
        height: '16px'
    },
    otherGoalText: {
        fontWeight: TypographyWeights.MEDIUM,
        textAlign: 'center',
        color: theme.palette.primary.dark
    },
    focusAreaContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        rowGap: '24px'
    },
    focusAreas: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '4px',
        width: '100%'
    },
    focusArea: {
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 12px',
        backgroundColor: theme.palette.primary.light,
        borderRadius: '8px',
        width: '100%'
    },
    focusAreaTitle: {
        fontWeight: TypographyWeights.BOLD,
        color: theme.palette.primary.dark
    },
    focusAreaSubtitle: {
        color: theme.palette.primary.dark
    },
    focusAreasText: {
        textAlign: 'center'
    },
    summaryContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        rowGap: '24px'
    },
    summary: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '8px',
        width: '100%'
    },
    item: {
        display: 'grid',
        gridTemplateColumns: '32px 1fr',
        width: '100%',
        alignItems: 'center',
        overflow: 'hidden',
        borderRadius: '32px 12px 12px 32px'
    },
    itemValue: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '64px',
        height: '64px',
        backgroundColor: theme.palette.primary.main,
        border: `4px solid ${theme.palette.white}`,
        borderRadius: '50%',
        fontWeight: TypographyWeights.BOLD,
        color: theme.palette.white,
        zIndex: 1
    },
    itemTextContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: '52px',
        width: '100%',
        minHeight: '46px',
        borderRadius: '12px',
        backgroundColor: theme.palette.primary['100']
    },
    summaryText: {
        textAlign: 'center',
        padding: '0 16px'
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: '0' 
        },
        '100%': {
            opacity: '1' 
        },
    },
    '@keyframes fadeOut': {
        '0%': {
            opacity: '1' 
        },
        '100%': {
            opacity: '0' 
        },
    },
    '@keyframes grow': {
        '0%': {
            transform: 'scale(0)' 
        },
        '100%': {
            transform: 'scale(1)' 
        },
    },
    '@keyframes slideLeft': {
        '0%': {
            transform: 'translate(-100vw, 0)' 
        },
        '100%': {
            transform: 'translate(0)' 
        },
    }
}));

const IDEAL_OUTCOMES = {
    ['Drink Less']: 'drinking less without quitting entirely.',
    ['Complete Zero']: 'cutting out alcohol entirely.',
    ['Not Sure']: 'finding the right balance for you.'
};

export const GoalRecap = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const [cookies] = useCookies();
    const topGoal = useMemo(() => formContext.answers['top_goal'], [formContext.answers['top_goal']]);
    const otherGoals = useMemo(() => formContext.answers['other_goals'], [formContext.answers['other_goals']]);
    const focusAreas = useMemo(() => formContext.answers['focus_areas'], [formContext.answers['focus_areas']]);
    const frequencyAnswer = useMemo(() => formContext.answers['freq_of_effects'], [formContext.answers['freq_of_effects']]);

    const drinksAnswer = useMemo(() => formContext.answers.totalDrinksBaseline, [formContext.answers.totalDrinksBaseline]);
    const targetDrinksAnswer = useMemo(() => formContext.answers.targetTotalDrinks, [formContext.answers.targetTotalDrinks]);
    const dryDaysAnswer = useMemo(() => formContext.answers.totalDryDaysBaseline, [formContext.answers.totalDryDaysBaseline]);
    const targetDryDaysAnswer = useMemo(() => formContext.answers.targetTotalDryDays, [formContext.answers.targetTotalDryDays]);
    const endGoalAnswer = useMemo(() => formContext.answers.endGoal, [formContext.answers.endGoal]);

    const classes = useStyles();
    const theme = useTheme();
    const [startTime, setStartTime] = useState(formContext.startTime);

    const SCREENS = useMemo(() => [
        (
            <Box
                key={0}
                className={classes.goalContainer}
            >
                <Box className={classes.iconContainer}>
                    <Box className={classes.topGoalIcon}>
                        {GOAL_CHOICES.find(choice => choice.key === topGoal)?.icon}
                    </Box>
                </Box>
                <Typography
                    variant={TypographyVariants.H5}
                    className={classes.topGoalTitle}
                    dangerouslySetInnerHTML={{
                        __html: `Our north star will be working together to <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">${GOAL_CHOICES.find(choice => choice.key === topGoal)?.action}.</span>` 
                    }}
                />
                {otherGoals.length > 0 && (
                    <>
                        <Box className={classes.divider} />
                        <Typography
                            variant={TypographyVariants.H5}
                            className={classes.topGoalSubtitle}
                        >
                            {'We\'ll focus on these areas as well:'}
                        </Typography>
                        <Box className={classes.otherGoals}>
                            {otherGoals.map(otherGoal => {
                                const goal = GOAL_CHOICES.find(choice => choice.key === otherGoal);
                                return (
                                    <Box
                                        key={otherGoal}
                                        className={classes.otherGoal}
                                    >
                                        <Box className={classes.otherGoalIcon}>
                                            {goal?.icon}
                                        </Box>
                                        <Typography
                                            variant={TypographyVariants.H5}
                                            className={classes.otherGoalText}
                                            style={{
                                                padding: `0 ${goal.padding}` 
                                            }}
                                        >
                                            {goal?.subtitle}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                )}
            </Box>
        ),
        (
            <Box
                key={1}
                className={classes.focusAreaContainer}
            >
                <Box className={classes.iconContainer}>
                    <Box className={classes.topGoalIcon}>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g clipPath="url(#clip0_4210_10066)">
                                <path
                                    d="M23.2336 20.9825L17.6818 15.4308C19.0743 13.4737 19.6807 11.0648 19.3807 8.68164C19.0806 6.29849 17.8962 4.11506 16.062 2.56417C14.2278 1.01329 11.8779 0.20816 9.47803 0.3084C7.07817 0.408639 4.80356 1.40692 3.10512 3.10537C1.40668 4.80381 0.408395 7.07841 0.308156 9.47828C0.207916 11.8781 1.01304 14.2281 2.56393 16.0622C4.11482 17.8964 6.29825 19.0809 8.6814 19.3809C11.0645 19.6809 13.4734 19.0746 15.4305 17.682L20.9823 23.2338C21.2808 23.5323 21.6857 23.7001 22.1079 23.7001C22.5301 23.7001 22.935 23.5323 23.2336 23.2338C23.5321 22.9353 23.6998 22.5304 23.6998 22.1082C23.6998 21.686 23.5321 21.2811 23.2336 20.9825ZM2.48102 9.90766C2.48102 8.43886 2.91657 7.00304 3.73259 5.78178C4.54862 4.56051 5.70846 3.60865 7.06546 3.04657C8.42245 2.48448 9.91566 2.33741 11.3562 2.62396C12.7968 2.91051 14.1201 3.61781 15.1587 4.65641C16.1973 5.69501 16.9046 7.01826 17.1911 8.45884C17.4777 9.89942 17.3306 11.3926 16.7685 12.7496C16.2064 14.1066 15.2546 15.2665 14.0333 16.0825C12.812 16.8985 11.3762 17.3341 9.90742 17.3341C7.9385 17.3318 6.05088 16.5487 4.65864 15.1564C3.26641 13.7642 2.48327 11.8766 2.48102 9.90766Z"
                                    fill={theme.palette.white}
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_4210_10066">
                                    <rect
                                        width="24"
                                        height="24"
                                        fill={theme.palette.white}
                                    />
                                </clipPath>
                            </defs>
                        </svg>
                    </Box>
                </Box>
                <Typography
                    variant={TypographyVariants.H5}
                    className={classes.focusAreasText}
                    dangerouslySetInnerHTML={{
                        __html: `Sunnyside will help you achieve your ideal outcome of <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">${IDEAL_OUTCOMES[endGoalAnswer]}</span>` 
                    }}
                />
                <Box className={classes.divider} />
                <Typography
                    variant={TypographyVariants.H5}
                    className={classes.topGoalSubtitle}
                >
                    {'We\'ll prioritize strategies for:'}
                </Typography>
                <Box className={classes.focusAreas}>
                    {focusAreas.map(focusArea => {
                        const focus_area = FOCUS_AREAS.find(focus_area => focus_area.key === focusArea);
                        return (
                            <Box
                                key={focusArea}
                                className={classes.focusArea}
                            >
                                <Typography
                                    variant={TypographyVariants.H5}
                                    className={classes.focusAreaTitle}
                                >
                                    {focus_area?.title}
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.focusAreaSubtitle}
                                >
                                    {focus_area?.subtitle}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        ),
        (
            <Box
                key={2}
                className={classes.summaryContainer}
            >
                <Box className={classes.summary}>
                    <Box className={classes.item}>
                        <Typography
                            variant={TypographyVariants.H2}
                            className={classes.itemValue}
                        >
                            {drinksAnswer}
                        </Typography>
                        <Box className={classes.itemTextContainer}>
                            <Typography
                                variant={TypographyVariants.H5}
                                dangerouslySetInnerHTML={{
                                    __html: `Total Weekly <span style="font-weight:${TypographyWeights.BOLD};">Drinks</span>` 
                                }}
                            />
                        </Box>
                    </Box>
                    <Box className={classes.item}>
                        <Typography
                            variant={TypographyVariants.H2}
                            className={classes.itemValue}
                        >
                            {dryDaysAnswer}
                        </Typography>
                        <Box className={classes.itemTextContainer}>
                            <Typography
                                variant={TypographyVariants.H5}
                                dangerouslySetInnerHTML={{
                                    __html: `Total Weekly <span style="font-weight:${TypographyWeights.BOLD};">Dry Days</span>` 
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
                <Typography
                    variant={TypographyVariants.H5}
                    className={classes.summaryText}
                    dangerouslySetInnerHTML={{
                        __html: `Today, you report feeling the negative effects of alcohol <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">${frequencyAnswer?.toLowerCase()}.</span>` 
                    }}
                />
            </Box>
        ),
        (
            <Box
                key={3}
                className={classes.summaryContainer}
            >
                <Box className={classes.summary}>
                    <Box className={classes.item}>
                        <Typography
                            variant={TypographyVariants.H2}
                            className={classes.itemValue}
                        >
                            {targetDrinksAnswer}
                        </Typography>
                        <Box className={classes.itemTextContainer}>
                            <Typography
                                variant={TypographyVariants.H5}
                                dangerouslySetInnerHTML={{
                                    __html: `Total Weekly <span style="font-weight:${TypographyWeights.BOLD};">Drinks</span>` 
                                }}
                            />
                        </Box>
                    </Box>
                    <Box className={classes.item}>
                        <Typography
                            variant={TypographyVariants.H2}
                            className={classes.itemValue}
                        >
                            {targetDryDaysAnswer}
                        </Typography>
                        <Box className={classes.itemTextContainer}>
                            <Typography
                                variant={TypographyVariants.H5}
                                dangerouslySetInnerHTML={{
                                    __html: `Total Weekly <span style="font-weight:${TypographyWeights.BOLD};">Dry Days</span>` 
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
                <Typography
                    variant={TypographyVariants.H5}
                    className={classes.summaryText}
                    dangerouslySetInnerHTML={{
                        __html: `With Sunnyside, we'll reduce the frequency of negative effects to <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">never or almost never.</span>` 
                    }}
                />
            </Box>
        )
    ], [topGoal, otherGoals, focusAreas, drinksAnswer, dryDaysAnswer, targetDrinksAnswer, targetDryDaysAnswer, endGoalAnswer, frequencyAnswer]);

    const [step, setStep] = useState(formContext.screenDirection === 'right' ? 3 : 0);
    const [stepTitle, setStepTitle] = useState(stepTitles[step]);
    const [slide, setSlide] = useState(true);
    const [direction, setDirection] = useState(formContext.screenDirection);
    const [content, setContent] = useState(SCREENS[step]);

    useEffect(() => {
        setContent(SCREENS[step]);
    }, [topGoal, otherGoals, focusAreas, drinksAnswer, dryDaysAnswer, targetDrinksAnswer, targetDryDaysAnswer, endGoalAnswer, frequencyAnswer]);

    const onClick = () => {
        if (step === 3) {
            formContext.goNext();
            return;
        }
        setStartTime(new Date().getTime());
        setStep(step + 1);
        setSlide(false);
        setDirection('right');
        if (scrollableContainer.current) scrollableContainer.current.scrollTo({
            top: 0,
            behaviour: 'smooth' 
        });
    };

    const onBackClick = () => {
        if (step === 0) {
            formContext.goBack();
            return;
        }
        setStartTime(null);
        setStep(step - 1);
        setSlide(false);
        setDirection('left');
        if (scrollableContainer.current) scrollableContainer.current.scrollTo({
            top: 0,
            behaviour: 'smooth' 
        });
    };

    useEffect(() => {
        if (step === 0) {
            formContext.fireTrackingEvents(formContext, 'goals recap', cookies);
        } else if (step === 1) {
            formContext.fireTrackingEvents(formContext, 'focus areas recap', cookies);
        } else if (step === 2) {
            formContext.fireTrackingEvents(formContext, 'starting point recap', cookies);
        } else if (step === 3) {
            formContext.fireTrackingEvents(formContext, 'ideal outcome', cookies);
        }
    }, [step]);

    useEffect(() => {
        if (slide) return;

        setTimeout(() => {
            setStepTitle(stepTitles[step]);
            setContent(SCREENS[step]);
        }, 500);
    }, [slide]);

    useEffect(() => {
        setDirection(direction === 'left' ? 'right' : 'left');
        setSlide(true);
    }, [content]);

    useEffect(() => {
        if (!startTime) return;

        const endTime = new Date().getTime();
        const diff = endTime - startTime;

        let url = '';
        switch (step) {
        case 0:
            url = 'goals recap';
            break;
        case 1:
            url = 'focus areas recap';
            break;
        case 2:
            url = 'starting point recap';
            break;
        case 3:
            url = 'ideal outcome';
            break;
        }

        dataDogLogger(
            'info',
            `Diagnostic screen ${url} rendered in ${diff}ms`,
            {
                frontendMetric: diff,
                url,
                rawData: {
                    formId: formContext.formId
                }
            }
        );
    }, [startTime]);

    const scrollableContainer = useRef(null);

    return (
        <CardContainer
            sectionKey={'plan'}
            goBack={onBackClick}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position + step}
                    sectionKey={'plan'}
                />
                <Box
                    ref={scrollableContainer}
                    className={classes.scrollableContainer}
                >
                    <Slide
                        in={true}
                        direction={formContext.screenDirection}
                        timeout={250}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box className={classes.content}>
                            <Typography
                                variant={TypographyVariants.H3}
                            >
                                {'To start, here\'s a quick recap of the goals we\'ll work together on:'}
                            </Typography>
                            <Box className={classes.progress}>
                                <Box
                                    className={classes.circle}
                                    style={{
                                        backgroundColor: step === 0 ? theme.palette.primary.main : theme.palette.primary['100'] 
                                    }}
                                />
                                <Box
                                    className={classes.circle}
                                    style={{
                                        backgroundColor: step === 1 ? theme.palette.primary.main : theme.palette.primary['100'] 
                                    }}
                                />
                                <Box
                                    className={classes.circle}
                                    style={{
                                        backgroundColor: step === 2 ? theme.palette.primary.main : theme.palette.primary['100'] 
                                    }}
                                />
                                <Box
                                    className={classes.circle}
                                    style={{
                                        backgroundColor: step === 3 ? theme.palette.primary.main : theme.palette.primary['100'] 
                                    }}
                                />
                            </Box>
                            <Slide
                                in={slide}
                                direction={direction}
                                timeout={250}
                                mountOnEnter
                            >
                                <Box
                                    style={{
                                        width: '100%' 
                                    }}
                                >
                                    <Box className={classes.textSection}>
                                        <Typography
                                            variant={TypographyVariants.H3}
                                            className={classes.stepTitle}
                                        >
                                            {stepTitle}
                                        </Typography>
                                    </Box>
                                    {content}
                                </Box>
                            </Slide>
                        </Box>
                    </Slide>
                    <Box
                        style={{
                            height: '500px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default GoalRecap;
